import React, { useContext } from "react";
import clsx from "clsx";
import { Form, Row, Col } from "react-bootstrap";

import { Skeleton, RequiredMark, SelectAsync } from "components";
import {
	RequiredMarkContext,
	ApplicationEntryProgressContext,
} from "components/contexts";
import { DebounceTimeout } from "globalConstants";

import { selectStyles } from "./selectStyles";

import Select from "./index";

import styles from "./selectAsync.module.scss";

const ONLY_BORDER_ERROR = "onlyBorderError";

const defaultProps = {
	labelSize: { sm: 6 },
	controlSize: { sm: 6 },
};

const SelectFormGroup = ({
	async,
	// Needed to manually control value
	valueAlias,
	input,
	meta,
	label,
	loadOptions,
	disableSkeleton,
	options,
	findOption,
	onChangeCb,
	blurLoading,
	preselect = {},
	// To display placeholder for manually controlled value pass null as valueAlias
	placeholder = "Please Select",
	disabled,
	hideLabel = false,
	hideRequiredMark,
	requiredMark = false,
	labelSize = defaultProps.labelSize,
	controlSize = defaultProps.controlSize,
	containerClassName,
	getOptionLabel,
	getOptionValue,
	...props
}) => {
	const Component = async ? SelectAsync : Select;

	const showRequiredMark =
		(useContext(RequiredMarkContext) && !hideRequiredMark && !disabled) ||
		requiredMark;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const handleChange = option => {
		const value = getOptionValue?.(option) ?? option?.value ?? null;
		input.onChange(value);
		if (onChangeCb) onChangeCb(option);
	};

	const handleBlur = () => {
		input.onBlur(input.value);
		triggerSave?.();
	};

	const showLoader =
		!disableSkeleton &&
		(blurLoading ||
			(blurLoading === undefined && !loadOptions && !options?.length));

	const warningMessage = meta.touched && meta.warning && (
		<Form.Control.Feedback className="d-block text-warning">
			{meta.warning}
		</Form.Control.Feedback>
	);

	const errorMessage = meta.error !== ONLY_BORDER_ERROR &&
		meta.touched &&
		meta.error && (
			<Form.Control.Feedback type="invalid" className="d-block">
				{meta.error}
			</Form.Control.Feedback>
		);

	const disabledClass = disabled && styles.disabled;

	return (
		<Row
			className={clsx(
				styles.selectFormGroup,
				containerClassName,
				disabledClass,
			)}
		>
			{!hideLabel && (
				<Form.Label
					column
					className={styles.selectLabel}
					{...labelSize}
				>
					{label}
					{showRequiredMark && <RequiredMark />}
				</Form.Label>
			)}

			<Col {...controlSize}>
				{showLoader ? (
					<Skeleton height="34px" />
				) : (
					<Component
						name={input.name}
						value={
							valueAlias === undefined ? input?.value : valueAlias
						}
						options={options}
						loadOptions={loadOptions}
						onChange={handleChange}
						onBlur={handleBlur}
						debounceTimeout={DebounceTimeout}
						cacheOptions
						additional={{ page: 0 }}
						hasError={meta.touched && meta.error}
						hasWarning={!!warningMessage}
						placeholder={placeholder}
						isDisabled={disabled}
						styles={selectStyles}
						getOptionLabel={getOptionLabel}
						getOptionValue={getOptionValue}
						inputId={props.inputId}
						{...props}
					/>
				)}
				<div className={styles.validation}>
					{!disabled && (errorMessage || warningMessage)}
				</div>
			</Col>
		</Row>
	);
};

export default SelectFormGroup;
